import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { blue, green, red, yellow } from "@material-ui/core/colors";
import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";
import WarningIcon from "@material-ui/icons/Warning";
import DescriptionIcon from "@material-ui/icons/Description";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import RegularButton from "components/CustomButtons/Button";
import { ModalV2 } from "components/Modal/ModalV2";
import { useToggle } from "hooks/useToggle";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";
import { downloadInvoiceFile } from "redux/actions/invoicesTransfer/invoicesTransferAction";
import { styles } from "./styles";
import { globalInvoiceStatus } from "./statusDictionary";
import { formatInvoiceJson } from "./formatInvoiceJson";

const { INVOICE_GLOBAL_JSON, INVOICE_GLOBAL_PDFXML } = PERMISSIONS;

const common = {
  headerStyle: styles().thTable,
  filterable: true,
  resizable: false,
};

const yellow1 = yellow[800];
const blue1 = blue[700];
const green1 = green[700];
const red1 = red[500];

const StatusCell = ({ status }) => {
  const config = globalInvoiceStatus[status] || {};
  return (
    <div
      style={{
        backgroundColor: config.bgColor,
        alignContent: "center",
        textAlign: "center",
        fontWeight: "bold",
        color: config.color,
        width: "100%",
        height: "100%",
      }}
    >
      {config.translate || status}
    </div>
  );
};


const ActionsCell = ({ original }) => {
  const dispatch = useDispatch();
  const [openError, setOpenError] = useToggle();
  const [openJson, setOpenJson] = useToggle();
  const [loadingXml, setLoadingXml] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const canViewJson = useRuleValidator(INVOICE_GLOBAL_JSON);
  const canDownloadFiles = useRuleValidator(INVOICE_GLOBAL_PDFXML);
  const { idInvoice, status, errorMessage, json } = original;
  const formattedJson = formatInvoiceJson(json);
  const isTimbrada = status === globalInvoiceStatus.TIMBRADA.value;

  const handleDownload = async (isPdf) => {
    isPdf ? setLoadingPdf(true) : setLoadingXml(true);
    await dispatch(downloadInvoiceFile(idInvoice, isPdf));
    isPdf ? setLoadingPdf(false) : setLoadingXml(false);
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {status === globalInvoiceStatus.ERROR.value && (
          <RegularButton
            size="sm"
            round
            justIcon
            color="transparent"
            tooltip="Ver error"
            onClick={setOpenError}
          >
            <WarningIcon style={{ color: yellow1 }} />
          </RegularButton>
        )}

        {canViewJson && (
          <RegularButton
            size="sm"
            round
            justIcon
            color="transparent"
            tooltip="Ver JSON enviado"
            onClick={setOpenJson}
            disabled={!json}
          >
            <DescriptionIcon style={{ color: blue1 }} />
          </RegularButton>
        )}

        {canDownloadFiles && isTimbrada && (
          <RegularButton
            size="sm"
            round
            justIcon
            color="transparent"
            tooltip="Descargar XML"
            onClick={() => handleDownload(false)}
            disabled={loadingXml}
          >
            {loadingXml ? (
              <CircularProgress size={18} style={{ color: green1 }} />
            ) : (
              <InsertDriveFileIcon style={{ color: green1 }} />
            )}
          </RegularButton>
        )}

        {canDownloadFiles && isTimbrada && (
          <RegularButton
            size="sm"
            round
            justIcon
            color="transparent"
            tooltip="Descargar PDF"
            onClick={() => handleDownload(true)}
            disabled={loadingPdf}
          >
            {loadingPdf ? (
              <CircularProgress size={18} style={{ color: red1 }} />
            ) : (
              <PictureAsPdfIcon style={{ color: red1 }} />
            )}
          </RegularButton>
        )}
      </div>

      {/* ModalV2 mete a sus hijos dentro de un Grid container (spacing=2):
          sin un Grid item que compense el margen negativo, el contenido se
          veia descuadrado y pegado al borde -de ahi el mal espaciado. */}
      <ModalV2
        open={openError}
        onClose={setOpenError}
        title="Error al timbrar"
        customWidth="40%"
        minHeight={120}
      >
        <Grid item xs={12}>
          {/* Alert ya trae el icono y el color rojo de severidad "error":
              deja claro de un vistazo que esto es una falla, no solo texto. */}
          <Alert severity="error" variant="filled">
            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
              {errorMessage}
            </Typography>
          </Alert>
        </Grid>
      </ModalV2>

      <ModalV2
        open={openJson}
        onClose={setOpenJson}
        title="JSON enviado"
        customWidth="min(90vw, 800px)"
        minHeight={200}
      >
        <Grid item xs={12}>
          <Box style={{ maxWidth: "100%" }}>
            <SyntaxHighlighter
              language="json"
              style={coy}
              wrapLongLines
              // wrapLongLines ya envuelve cada linea en un span, pero el
              // theme de Prism fuerza white-space:pre en el <code> -sin
              // pisarlo aqui, los valores largos (ids, uuids) seguian
              // generando el scroll horizontal que no queriamos.
              codeTagProps={{
                style: { whiteSpace: "pre-wrap", wordBreak: "break-word" },
              }}
              customStyle={{
                margin: 0,
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowX: "hidden",
                // 80vh deja el JSON completo visible sin scroll en la gran
                // mayoria de los casos -un solo registro de la cola-;
                // overflowY queda como red de seguridad si es mas grande.
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              {formattedJson || String(json)}
            </SyntaxHighlighter>
          </Box>
        </Grid>
      </ModalV2>
    </>
  );
};

export const columns = [
  {
    ...common,
    Header: "Fecha Emisión",
    accessor: "date",
    filterMethod: (filter, row) =>
      row.date?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Permiso",
    accessor: "creLicense",
    filterMethod: (filter, row) =>
      row.creLicense?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Método Pago",
    accessor: "payment",
    filterMethod: (filter, row) =>
      row.payment?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "IVA",
    accessor: "taxRate",
    Cell: ({ value }) => `${value}%`,
    filterMethod: (filter, row) => String(row.taxRate).includes(filter.value),
  },
  {
    ...common,
    Header: "Status",
    accessor: "status",
    getProps: () => ({ style: { margin: 0, paddingTop: 0, paddingBottom: 0 } }),
    Cell: ({ value }) => <StatusCell status={value} />,
    // La celda muestra la traduccion ("Pendiente de envío"): se busca por
    // ella y por el valor interno.
    filterMethod: (filter, row) => {
      const search = filter.value?.toLowerCase();
      const translated = globalInvoiceStatus[row.status]?.translate;
      return (
        row.status?.toLowerCase().includes(search) ||
        !!translated?.toLowerCase().includes(search)
      );
    },
  },
  {
    ...common,
    Header: "Acciones",
    filterable: false,
    Cell: ({ original }) => <ActionsCell original={original} />,
  },
];
